<script setup>
import { computed, onBeforeUnmount, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import StudentAttendanceDialog from '@/components/Dialogs/Forms/StudentAttendanceDialog.vue';
import StudentDisciplinaryDialog from '@/components/Dialogs/Forms/StudentDisciplinaryDialog.vue';
import ViewStudentClassRecordDialog from '@/components/Dialogs/Views/ViewStudentClassRecordDialog.vue';
import eventBus from '@/eventBus';
import { apiRequest } from '@/api/http';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { useTerm } from '@/composables/useTerm';
import UiTermSelect from '@/components/ui/UiTermSelect.vue';
import { titleCase } from '@/utils/format';

useSessionGuard();

// A teacher's classes per subject (tab), with attendance, class record
// and incident report for each class. The tabs are the subjects on their
// schedule for the school year and, in a year graded in terms, the chosen
// term (each term has its own schedule).
const store = useStore();
const year = computed(() => store.getters.getFilterSelected);
const { hasTerms, terms, term, termQuery } = useTerm();
const subjects = shallowRef([]);
const subject = shallowRef(null);

async function loadSubjects() {
  try {
    const rows = await apiRequest('get', `/subjects/getMySubjects?schoolYearId=${year.value}&${termQuery.value}`);
    const list = Array.isArray(rows) ? rows : [];
    subjects.value = list.map((s) => ({ id: s.id, name: titleCase(s.subject_title) }));
  } catch {
    subjects.value = [];
  }
  // Keep the open subject when it's taught this term too.
  if (!subjects.value.some((s) => s.id === subject.value)) subject.value = subjects.value[0]?.id ?? null;
}
watch([year, term], loadSubjects, { immediate: true });

const { items, loading, error, load } = useListResource(async () => {
  if (!subject.value) return [];
  const rows = await apiRequest('get', `/subjects/getMyClassRecord/${year.value}/${subject.value}?${termQuery.value}`);
  if (!Array.isArray(rows)) throw new Error('Could not load your classes.');
  return rows.map((r) => ({ ...r, room_section: (r.room_section || '').toUpperCase() }));
});
watch([subject, year, term], load);

const headers = [
  { title: 'Class', key: 'room_section' },
  { title: 'Grade', key: 'grade_level' },
  { title: 'Enrolled students', key: 'enrolled', align: 'end' },
];

// Attendance uses the newer v-model dialog; the others are opened by
// handing them a fresh object.
const attendanceFor = shallowRef(null);
const attendanceOpen = shallowRef(false);
const recordData = shallowRef(null);
// Reports use the newer v-model dialog.
const reportFor = shallowRef(null);
const reportOpen = shallowRef(false);
const action = shallowRef(null);

function open(target, row, mode = 'View') {
  action.value = mode;
  target.value = { ...row };
}

const rowActions = (row) => [
  {
    title: 'Attendance',
    icon: 'mdi-calendar-check-outline',
    onClick: () => {
      attendanceFor.value = row;
      attendanceOpen.value = true;
    },
  },
  // The class record opens on the chosen term.
  { title: 'Class record', icon: 'mdi-notebook-outline', onClick: () => open(recordData, { ...row, term: term.value }) },
  {
    title: 'File a report',
    icon: 'mdi-alert-octagon-outline',
    onClick: () => {
      reportFor.value = row;
      reportOpen.value = true;
    },
  },
];

const DIALOG_EVENTS = ['closeStudentAttendanceDialog', 'closeStudentClassRecordDialog', 'closeStudentReportDialog'];
DIALOG_EVENTS.forEach((e) => eventBus.on(e, load));
onBeforeUnmount(() => DIALOG_EVENTS.forEach((e) => eventBus.off(e, load)));
</script>

<template>
  <UiTableCard
    v-model:tab="subject"
    title="Class records"
    description="Attendance, grades and reports for the classes you teach."
    :tabs="subjects"
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error"
    search-placeholder="Search classes"
    :empty-text="subjects.length ? 'No classes for this subject.' : `You have no classes scheduled${term ? ` in the ${term}` : ''}.`"
    row-label="room_section"
    :row-actions="rowActions"
  >
    <template v-if="hasTerms" #toolbar>
      <UiTermSelect v-model="term" :items="terms" density="compact" />
    </template>
    <template #[`item.room_section`]="{ item }">
      <span class="font-weight-medium">{{ item.room_section }}</span>
    </template>
    <template #[`item.enrolled`]="{ item }">
      <span v-if="item.enrolled">{{ item.enrolled }} {{ item.enrolled === 1 ? 'student' : 'students' }}</span>
      <span v-else class="text-medium-emphasis">None yet</span>
    </template>
  </UiTableCard>

  <StudentAttendanceDialog v-model="attendanceOpen" :class-info="attendanceFor" @saved="load" />
  <ViewStudentClassRecordDialog :data="recordData" :action="action" />
  <StudentDisciplinaryDialog v-model="reportOpen" :class-info="reportFor" />
</template>
